import './offer-step.scss';

import { Segmented } from 'antd';
import { notification } from 'antd/lib';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';

import { CustomModal } from '@/components/Shared/Modal/CustomModal';
import { Button } from '@/components/Shared/ui';
import { BankNames, DictTypes, SELECTED_BANK_STATUSES } from '@/constants';
import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import { downloadFilesApi } from '@/integrations/index.api';
// import { downloadElementAsPDF } from '@/services/helpers/DOM';
import { ReloadOutlined } from '@/media/antd-icons';
// import DownloadIcon from '@/media/icons/download.svg?react';
import ReloadOutlinedIcon from '@/media/icons/recolsilation.svg?react';
import { formatPrice } from '@/services/formatter';
import { handleError } from '@/services/handleError';
import { useAppDispatch, useAppSelector } from '@/store';
import { onSetSelectedBank } from '@/store/financingStep.slice';
import { startGlobalLoading, stopGlobalLoading } from '@/store/loading.slice';
import { changeRequestStatus, setPageTitle } from '@/store/newOrder.slice';
import {
  acceptLeasingOffer,
  acceptOffer,
  changeOfferStatus,
  getEuBankStatus,
  getForteStatus,
  getResponsesByRequestId
} from '@/store/offerStep.slice';
import type { TAutoCredLeasingResponse } from '@/types/newOrder';

import { getVehicleFormKey } from '../helpers/helpers';
import { mapBankOfferFromApi } from '../helpers/mapBankOfferFromApi';
import useAutoCredRequestId from '../hooks/useAutoCredRequestId';
import useRefreshOrderData from '../hooks/useRefreshOrderData';
import {
  AcceptModal,
  LeasingAcceptModal,
  type LeasingAcceptedOfferInterface,
  RecreateRequest
} from './entities';
import type {
  IAcceptOfferDto,
  IBccResponseSendedBody,
  TBankOffers
} from './types';
import { BankOffers, LeasingOffers } from './widgets';

const BANKS_WITH_DOWNLOAD_FILES = [BankNames.sapa, BankNames.bereke];

const acceptedStatuses = [
  ...SELECTED_BANK_STATUSES,
  'client_accepted',
  'approved',
  'accepted_alternative',
  'pre_approved'
];

const selectedBankStatusIndexMap = new Map(
  SELECTED_BANK_STATUSES.map((status, index) => [status, index])
);

const acceptedStatusesIndexMap = new Map(
  acceptedStatuses.map((status, index) => [status, index])
);

interface OfferStepContextInterface {
  handleChangeEuBankStatus: (responseExtUuid: string) => Promise<void>;
  handleChangeForteStatus: (responseExtUuid: string) => Promise<boolean>;
  onAcceptOffer: (data: TBankOffers) => void;
  onAcceptLeasingOffer: (data: TAutoCredLeasingResponse) => void;
  onNewAcceptModal: (data: IAcceptOfferDto) => void;
  handleCancelResponse: (bankResponse: TBankOffers) => Promise<void>;
  updateResponses: (shouldNotNotify: boolean) => void;
}
export const OfferStepContext = createContext<OfferStepContextInterface>({
  handleChangeEuBankStatus: async () => {},
  handleChangeForteStatus: async () => false,
  onAcceptOffer: () => {},
  onAcceptLeasingOffer: () => {},
  onNewAcceptModal: () => {},
  handleCancelResponse: async () => {},
  updateResponses: () => {}
});

type TOfferStepProps = { handleAcceptOffer: () => void };

const statusColors = {
  approved: '#0CAF60',
  waiting: '#ffd023',
  alternative: '#2f78ee',
  decline: '#e03137',
  error: '#a8a8a8'
};

export const OfferStep = ({ handleAcceptOffer }: TOfferStepProps) => {
  const { dicts } = useAppSelector((state) => state.dict);
  const { clientInfo, loan, leasingOrder, orderApp } = useAppSelector(
    (state) => state.newOrder
  );
  const { sentTo = 'banks_leasing' } = orderApp || {};
  const {
    userInfo,
    userAccesses: {
      global: { isAdmin },
      autoCredit: { canResendRequest }
    }
  } = useAppSelector((state) => state.userInfo);
  const { banksOffers, leasingOffers } = useAppSelector(
    (state) => state.offerStep
  );
  const { selectedBank, selectedLeasing } = useAppSelector(
    (state) => state.financingStep
  );
  const dispatch = useAppDispatch();

  const { requestId } = useAutoCredRequestId();
  const { refreshOrderData } = useRefreshOrderData({
    allowWithOrder: true,
    autoFetch: false,
    requestId
  });

  const [focusedBankOffer, setFocusedBankOffer] = useState<TBankOffers | null>(
    null
  );
  const [focusedLeasingOffer, setFocusedLeasingOffer] =
    useState<TAutoCredLeasingResponse | null>(null);
  const [newFocusedBankOffer, setNewFocusedBankOffer] =
    useState<IAcceptOfferDto | null>(null);

  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showLeasingAcceptModal, setShowLeasingAcceptModal] = useState(false);
  const [showInfoForForte, setShowInfoForForte] = useState(false);
  const [reCreateModal, setReCreateModal] = useState(false);

  const [responsesUpdating, setResponsesUpdating] = useState(false);
  const [isAcceptLoading, setAcceptLoading] = useState(false);
  const [isLeasingAcceptLoading, setLeasingAcceptLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'banks' | 'leasing'>('banks');
  const skipOffersTabChange = useRef<boolean>(false);

  // const [isDownloading, setIsDownloading] = useState(false);
  const [banksPriorityStatus, setBanksPriorityStatus] = useState<{
    count: number;
    type: 'approved' | 'waiting' | 'alternative' | 'decline' | 'error';
  }>({
    count: 0,
    type: 'waiting'
  });
  const [leasingPriorityStatus, setLeasingPriorityStatus] = useState<{
    count: number;
    type: 'approved' | 'waiting' | 'alternative' | 'decline' | 'error';
  }>({
    count: 0,
    type: 'waiting'
  });

  const isHasFinancedOffer: boolean = useMemo(
    () =>
      banksOffers.some((offer) => offer.status.code === 'financed') ||
      leasingOffers.some((offer) => offer.status.code === 'financed'),
    [banksOffers, leasingOffers]
  );

  // Вычисление массивов для банков
  const banksApprovedOffers = useMemo(() => {
    if (banksOffers.length === 0) return [];
    return banksOffers
      .filter((bank) => acceptedStatusesIndexMap.has(bank.status.code))
      .sort((a, b) => {
        const isSelectedA = selectedBankStatusIndexMap.has(a.status.code);
        const isSelectedB = selectedBankStatusIndexMap.has(b.status.code);

        if (isSelectedA && isSelectedB) {
          const indexA = selectedBankStatusIndexMap.get(a.status.code) || -1;
          const indexB = selectedBankStatusIndexMap.get(b.status.code) || -1;
          return indexA - indexB;
        }
        if (isSelectedA) {
          return -1;
        }
        if (isSelectedB) {
          return 1;
        }

        const isShinhanA = a.bank.name === 'shinhan';
        const isShinhanB = b.bank.name === 'shinhan';

        if (isShinhanA && isShinhanB) {
          const indexA = acceptedStatusesIndexMap.get(a.status.code) || -1;
          const indexB = acceptedStatusesIndexMap.get(b.status.code) || -1;
          return indexA - indexB;
        }
        if (isShinhanA) {
          return -1;
        }
        if (isShinhanB) {
          return 1;
        }

        const indexA = acceptedStatusesIndexMap.get(a.status.code) || -1;
        const indexB = acceptedStatusesIndexMap.get(b.status.code) || -1;
        return indexA - indexB;
      });
  }, [banksOffers]);

  const banksWaitingOffers = useMemo(() => {
    if (banksOffers.length > 0) {
      const waitArrayHandler = banksOffers.filter((bank) => {
        return (
          bank.status.code === 'waiting_bank_response' ||
          bank.status.code === 'on_verification'
        );
      });
      const filteredBanks =
        dicts?.Banks?.content.filter((bank) => bank.is_active) || [];

      return waitArrayHandler.filter((offer) =>
        filteredBanks.some((bank) => offer.bank.name.includes(bank.name))
      );
    } else {
      const banksDictContent = dicts?.[DictTypes.Banks]?.content || [];
      return [...banksDictContent]
        ?.filter((bank) => bank?.is_active)
        ?.map((bank) => {
          const offerHandler: TBankOffers = {
            bankName: bank.name,
            bank: {
              name: bank.name,
              uuid: bank.uuid,
              fullname: bank.fullname,
              is_active: !!bank.is_active,
              code: String(bank.code),
              icon: bank.icon || ''
            },
            hasApprovedDate: false,
            responsePrimaryBody: '',
            offerId: `${bank.uuid}-${bank.name}`,
            offeredUuid: '',
            status: {
              name: 'В ожидании предложений',
              code: 'waiting_bank_response',
              uuid: ''
            },
            extId: '',
            id: bank.name,
            responseApprovedBody: '',
            alternatives: [],
            initialPayment: '',
            insuranceAmount: '',
            loanAmount: '',
            monthlyPayment: '',
            product: '',
            rate: '',
            loanPeriod: '',
            responseSendedBody: ''
          };
          return offerHandler;
        });
    }
  }, [banksOffers, dicts]);

  const banksDeclinedOffers = useMemo(() => {
    if (banksOffers.length === 0) return [];
    return banksOffers.filter(
      (bank) =>
        bank.status.code === 'declined' ||
        bank.status.code === 'cancelled' ||
        bank.status.code === 'error'
    );
  }, [banksOffers]);

  // Вычисление массивов для лизинга
  const leasingOffersArrays = useMemo(() => {
    const leasingOffersMap: Record<
      'approved' | 'declined' | 'waiting',
      TAutoCredLeasingResponse[]
    > = {
      approved: [],
      declined: [],
      waiting: []
    };

    leasingOffers.forEach((leasingResponse) => {
      const statusCode = leasingResponse?.status?.code;

      if (!statusCode) return;

      switch (statusCode) {
        case 'accepted_alternative':
        case 'approved':
        case 'client_accepted':
        case 'financed':
        case 'waiting_of_financing':
          leasingOffersMap.approved.push(leasingResponse);
          break;
        case 'bank_response_timeout':
        case 'cancelled':
        case 'declined':
        case 'error':
          leasingOffersMap.declined.push(leasingResponse);
          break;
        case 'waiting_bank_response':
          leasingOffersMap.waiting.push(leasingResponse);
          break;
        default:
          break;
      }
    });

    return {
      approvedOffers: leasingOffersMap.approved,
      declinedOffers: leasingOffersMap.declined,
      waitingOffers: leasingOffersMap.waiting
    };
  }, [leasingOffers]);

  // Установка приоритетных статусов для банков
  useEffect(() => {
    if (banksApprovedOffers.length > 0) {
      setBanksPriorityStatus({
        count: banksApprovedOffers.length,
        type: 'approved'
      });
    } else if (banksWaitingOffers.length > 0) {
      setBanksPriorityStatus({
        count: banksWaitingOffers.length,
        type: 'waiting'
      });
    } else if (banksDeclinedOffers.length > 0) {
      setBanksPriorityStatus({
        count: banksDeclinedOffers.length,
        type: 'decline'
      });
    }
  }, [banksApprovedOffers, banksWaitingOffers, banksDeclinedOffers]);

  // Установка приоритетных статусов для лизинга
  useEffect(() => {
    if (leasingOffersArrays.approvedOffers.length > 0) {
      setLeasingPriorityStatus({
        count: leasingOffersArrays.approvedOffers.length,
        type: 'approved'
      });
    } else if (leasingOffersArrays.waitingOffers.length > 0) {
      setLeasingPriorityStatus({
        count: leasingOffersArrays.waitingOffers.length,
        type: 'waiting'
      });
    } else if (leasingOffersArrays.declinedOffers.length > 0) {
      setLeasingPriorityStatus({
        count: leasingOffersArrays.declinedOffers.length,
        type: 'decline'
      });
    }
  }, [leasingOffersArrays]);

  useEffect(() => {
    if (skipOffersTabChange.current) {
      return;
    }

    const statusPriority: Record<
      | (typeof banksPriorityStatus)['type']
      | (typeof leasingPriorityStatus)['type'],
      number
    > = {
      approved: 3,
      alternative: 2,
      waiting: 2,
      decline: 1,
      error: 1
    };

    const banksPriority: number = statusPriority[banksPriorityStatus.type] || 0;
    const leasingPriority: number =
      statusPriority[leasingPriorityStatus.type] || 0;

    if (selectedBank) {
      setActiveTab('banks');
    } else if (selectedLeasing) {
      setActiveTab('leasing');
    } else if (banksPriority > leasingPriority) {
      setActiveTab('banks');
    } else if (leasingPriority > banksPriority) {
      setActiveTab('leasing');
    } else if (banksPriority > 0) {
      setActiveTab('banks');
    } else if (leasingPriority > 0) {
      setActiveTab('leasing');
    }
  }, [
    banksPriorityStatus,
    leasingPriorityStatus,
    selectedBank,
    selectedLeasing
  ]);

  const showTabs = sentTo === 'banks_leasing';

  const pageTitle = useMemo(() => {
    if (!showTabs) {
      if (sentTo === 'banks') return 'Предложения от банков';
      if (sentTo === 'leasing') return 'Предложения от лизинговых компаний';
      return 'Предложения';
    }
    return 'Предложения';
  }, [showTabs, sentTo]);

  const selectedOfferModal = useMemo(() => {
    const data = newFocusedBankOffer || focusedBankOffer;
    if (!data) return null;
    // Собираем нужные данные для отображения в модальном окне
    return {
      id: newFocusedBankOffer?.responseId || focusedBankOffer?.id || '',
      extId: newFocusedBankOffer?.offerId || focusedBankOffer?.extId || '',
      icon: data.bank?.icon || '',
      name: data.bank?.fullname || '',
      monthlyPayment: formatPrice(data.monthlyPayment),
      rate: Number(data.rate),
      loanAmount: formatPrice(data.loanAmount),
      initialPayment: formatPrice(data.initialPayment),
      loanPeriod: data.loanPeriod,
      auto: data.product,
      additionalConditions:
        'additionalConditions' in data ? data.additionalConditions : undefined
    };
  }, [focusedBankOffer, newFocusedBankOffer]);

  const selectedLeasingOfferModal: LeasingAcceptedOfferInterface | null =
    useMemo(() => {
      if (!focusedLeasingOffer || !leasingOrder) return null;

      return {
        auto: loan.auto || '',
        balloonAmount:
          focusedLeasingOffer.balloonAmount !== null
            ? formatPrice(focusedLeasingOffer.balloonAmount)
            : '-',
        initialFee: formatPrice(
          focusedLeasingOffer.initialPayment || leasingOrder.initialPayment
        ),
        leasingCompanyIcon: focusedLeasingOffer.leasingCompany.icon,
        leasingCompanyName: focusedLeasingOffer.leasingCompany.name,
        loanAmount:
          focusedLeasingOffer.approvedAmount !== null
            ? formatPrice(focusedLeasingOffer.approvedAmount)
            : '-',
        monthlyPayment: formatPrice(
          focusedLeasingOffer.firstMonthPayment || ''
        ),
        rate: String(focusedLeasingOffer.rate ?? ''),
        term: String(focusedLeasingOffer.loanPeriod ?? '')
      };
    }, [focusedLeasingOffer, leasingOrder, loan]);

  useEffect(() => {
    dispatch(setPageTitle(pageTitle));
  }, [pageTitle]);

  const handleChangeEuBankStatus = async (responseExtUuid: string) => {
    try {
      const data = {
        requestId: requestId,
        responseExtUuid: responseExtUuid
      };
      await dispatch(getEuBankStatus(data));
      notification.success({
        title: '',
        description: 'Статусы банка обновлены',
        duration: 5
      });
    } catch (error) {
      handleError(error, {
        customTitle: 'Ошибка обновления статуса'
      });
    }
  };

  const handleChangeForteStatus = async (responseExtUuid: string) => {
    try {
      const response = await dispatch(
        getForteStatus({ extUuid: responseExtUuid })
      );
      if ('error' in response && response.error) return false;
      return true;
    } catch {
      return false;
    }
  };

  const updateResponses = async (forte: boolean = false) => {
    setResponsesUpdating(true);
    try {
      skipOffersTabChange.current = true;
      await refreshOrderData(requestId);

      setTimeout(() => {
        skipOffersTabChange.current = false;
      }, 100);

      if (!forte) {
        notification.success({
          title: 'Статусы успешно обновлены',
          description:
            sentTo === 'banks'
              ? 'Статусы ответов банков были успешно обновлены'
              : sentTo === 'leasing'
                ? 'Статусы ответов лизинговых компаний были успешно обновлены'
                : 'Статусы ответов банков и лизинговых компаний были успешно обновлены',
          duration: 5
        });
      }
    } catch (error) {
      skipOffersTabChange.current = false;
      handleError(error, {
        customTitle: 'Произошла ошибка при обновлении статусов',
        customDescription: 'Пожалуйста попробуйте позже'
      });
    } finally {
      setResponsesUpdating(false);
    }
  };

  const onAcceptOffer = async (data: TBankOffers) => {
    setFocusedBankOffer(data);
    if (data.bankName === BankNames.forte && data.extId) {
      dispatch(startGlobalLoading('fullscreen'));
      const response = await dispatch(getForteStatus({ extUuid: data.extId }));
      dispatch(stopGlobalLoading('fullscreen'));
      if (getForteStatus.fulfilled.match(response)) {
        const updatedOffers = response.payload;

        const forteOffer = updatedOffers.find(
          (offer) => offer.ext_uuid === data.extId
        );
        if (
          forteOffer?.status.code === 'declined' ||
          forteOffer?.status.code === 'cancelled' ||
          forteOffer?.status.code === 'error'
        ) {
          updateResponses(true);
          setShowInfoForForte(true);
          return;
        }
        setShowAcceptModal(true);
      } else {
        notification.error({
          title: 'Запрос отклонен или завершился с ошибкой'
        });
      }
    } else {
      setShowAcceptModal(true);
    }
  };

  const onAcceptLeasingOffer = async (data: TAutoCredLeasingResponse) => {
    setFocusedLeasingOffer(data);
    setShowLeasingAcceptModal(true);
  };

  const onSelectLeasingOffer = async () => {
    if (!focusedLeasingOffer || !dicts) return;
    setLeasingAcceptLoading(true);

    try {
      const acceptLeasingOfferResponse = await dispatch(
        acceptLeasingOffer(focusedLeasingOffer.uuid)
      );

      if (acceptLeasingOffer.rejected.match(acceptLeasingOfferResponse)) {
        setLeasingAcceptLoading(false);
        notification.error({
          title: 'Произошла ошибка',
          description:
            'Не получилось принять предложение. Пожалуйста попробуйте позже'
        });
        return;
      }

      const changeRequestStatusResponse = await dispatch(
        changeRequestStatus({
          reqId: requestId,
          newStatus: 'waiting_of_financing'
        })
      );

      if (changeRequestStatus.rejected.match(changeRequestStatusResponse)) {
        setLeasingAcceptLoading(false);
        notification.error({
          title: 'Произошла ошибка',
          description:
            'Не получилось принять предложение. Пожалуйста попробуйте позже'
        });
        return;
      }

      await refreshOrderData(requestId);
      handleAcceptOffer();
    } catch {
      notification.error({
        title: 'Произошла ошибка',
        description:
          'Не получилось принять предложение. Пожалуйста попробуйте позже'
      });
    } finally {
      setLeasingAcceptLoading(false);
    }
  };

  const handleNewSelectOffer = async () => {
    if (!newFocusedBankOffer) return;
    setAcceptLoading(true);

    try {
      await dispatch(
        acceptOffer({
          responseId: newFocusedBankOffer.responseId,
          offerId: newFocusedBankOffer.offerId
        })
      );
      const response = await dispatch(
        getResponsesByRequestId({ reqId: requestId })
      );
      if ('error' in response && response.error) return;
      if (getResponsesByRequestId.fulfilled.match(response)) {
        const banksOffers = response.payload.map(mapBankOfferFromApi);
        const offer = banksOffers.find(
          (offer) => offer.offerId === newFocusedBankOffer.offerId
        );
        if (offer) dispatch(onSetSelectedBank(offer));
      }

      handleAcceptOffer();
    } catch (error) {
      handleError(error, {
        customTitle:
          'Не получилось принять предложение. Пожалуйста, попробуйте позже'
      });
    }

    setAcceptLoading(false);
  };

  const onSelectOffer = async () => {
    if (!focusedBankOffer) return;
    setAcceptLoading(true);
    await dispatch(
      changeOfferStatus({
        bankId: focusedBankOffer.id,
        is_offered: true,
        offered_uuid: focusedBankOffer.offerId,
        status:
          focusedBankOffer.bankName === BankNames.altyn
            ? 'mobile_client'
            : 'waiting_of_financing'
      })
    );
    await dispatch(
      changeRequestStatus({
        reqId: requestId,
        newStatus:
          focusedBankOffer.bankName === BankNames.altyn
            ? 'mobile_client'
            : 'waiting_of_financing'
      })
    );
    await refreshOrderData(requestId);
    dispatch(onSetSelectedBank(focusedBankOffer));

    let downloadFileDto: Record<string, unknown> = {
      request_uuid: requestId,
      response_uuid: focusedBankOffer?.id || '',
      bank_id: focusedBankOffer?.bank.code?.toString() || '',
      iin: clientInfo?.iin || ''
    };

    if (focusedBankOffer.bankName === BankNames.halyk) {
      if (!focusedBankOffer.responseApprovedBody) return;
      const responseApprovedBody = JSON.parse(
        focusedBankOffer?.responseApprovedBody
      );
      const offerType = responseApprovedBody?.additional[0]?.offerType || '';
      downloadFileDto = {
        ...downloadFileDto,
        bank_payload: {
          offer_type: offerType?.toString() || ''
        },
        name: 'guaranteeCert.pdf',
        type: 'guaranteeHalyk'
      };
    } else if (focusedBankOffer.bankName === BankNames.forte) {
      if (!focusedBankOffer.responseApprovedBody) return;
      handleChangeForteStatus(focusedBankOffer.extId || '');
      const responseApprovedBody = JSON.parse(
        focusedBankOffer?.responseApprovedBody
      );
      downloadFileDto = {
        ...downloadFileDto,
        bank_payload: {
          offer_type: ''
        },
        name: 'forteCommitmentLetter.pdf',
        type: 'commitment_letter',
        marital_status: responseApprovedBody?.marital_status_id || '',
        commitment_letter: responseApprovedBody?.commitment_letter_id || '',
        spouse_agreement: responseApprovedBody?.spouse_agreement_id || '',
        no_spouce_agreement: responseApprovedBody?.no_spouse_agreement_id || '',
        responseApprovedBody: responseApprovedBody
      };
    } else if (focusedBankOffer.bankName === BankNames.shinhan) {
      downloadFileDto = {
        ...downloadFileDto,
        type: 'guaranteeShinhan',
        name: 'guaranteeShinhan.pdf',
        bank_payload: {
          ext_uuid: focusedBankOffer.extId || '',
          type: 'guaranteeShinhan',
          name: 'guaranteeShinhan.pdf'
        }
      };
    } else if (focusedBankOffer.bankName === BankNames.ffb) {
      downloadFileDto = {
        ...downloadFileDto,
        bank_payload: {
          subject_identifier: focusedBankOffer.offerId,
          is_pre_approved: focusedBankOffer.status.code === 'pre_approved'
        }
      };
    } else if (focusedBankOffer.bankName === BankNames.bcc) {
      let sendedBody;
      if (focusedBankOffer.responseSendedBody?.trim())
        sendedBody = JSON.parse(focusedBankOffer.responseSendedBody) as
          IBccResponseSendedBody | { response: string };
      if (sendedBody && typeof sendedBody === 'string')
        sendedBody = JSON.parse(sendedBody) as IBccResponseSendedBody;
      if (sendedBody && 'response' in sendedBody)
        sendedBody = JSON.parse(sendedBody.response) as IBccResponseSendedBody;

      downloadFileDto = {
        ...downloadFileDto,
        type: 'grnBcc',
        name: 'guarantee_letter.pdf',
        bank_payload: {
          businessKey: sendedBody?.businessKey || '',
          processId: sendedBody?.processId || '',
          code: focusedBankOffer.bankName,
          viewIdBroker: sendedBody?.viewIdBroker || '',
          type: 'grnBcc',
          name: 'guarantee_letter.pdf'
        }
      };
    } else if (focusedBankOffer.bankName === BankNames.euBank) {
      await downloadFilesApi.downloadEubankFilesManually(
        focusedBankOffer?.id || ''
      );
      setTimeout(async () => {
        handleAcceptOffer();
        setAcceptLoading(false);
      }, 10000);
      return; // Остановка выполнения функции
    } else if (focusedBankOffer.bankName === BankNames.altyn) {
      downloadFileDto = {
        ...downloadFileDto,
        type: 'grnAltyn',
        name: 'offerAltyn.pdf',
        bank_payload: {
          subject_identifier: focusedBankOffer.offerId,
          type: 'grnAltyn',
          name: 'offerAltyn.pdf'
        }
      };
    } else if (focusedBankOffer.bankName === BankNames.solva) {
      downloadFileDto = {
        ...downloadFileDto,
        type: 'guaranteeSolva',
        name: 'guaranteeSolva.pdf'
      };
    } else if (focusedBankOffer.bankName === BankNames.mfo) {
      downloadFileDto = {
        ...downloadFileDto,
        type: 'guaranteeMfo',
        name: 'guaranteeMfo.pdf'
      };
    } else if (
      !BANKS_WITH_DOWNLOAD_FILES.includes(
        focusedBankOffer.bankName as BankNames
      )
    ) {
      handleAcceptOffer();
      setAcceptLoading(false);
      return; // Остановка выполнения функции
    }

    setTimeout(async () => {
      await downloadFilesApi.downloadAdditionalFiles([downloadFileDto]);
      handleAcceptOffer();
      setAcceptLoading(false);
    }, 10000);
  };

  const handleCancelResponse = async (bankResponse: TBankOffers) => {
    if (!userInfo) return;

    try {
      await autoCreditApi.cancelBankResponse(bankResponse.id);
      // Очищаем данные по транспортному средству
      sessionStorage.removeItem(getVehicleFormKey(userInfo.uuid, requestId));
      notification.success({
        title: 'Предложение успешно отклонено'
      });
      updateResponses(true);
    } catch (error) {
      handleError(error, {
        customTitle: 'Произошла ошибка при отмене предложения'
      });
    }
  };

  // const handleDownloadPDF = async () => {
  //   const bankOffers = document.getElementById('bank-offers');
  //   if (bankOffers) {
  //     setIsDownloading(true);
  //     try {
  //       await downloadElementAsPDF(bankOffers, 'bank-offers.pdf');
  //     } catch (error) {
  //       handleError(error, {
  //         customTitle: 'Произошла ошибка при скачивании PDF'
  //       });
  //     }
  //     setIsDownloading(false);
  //   }
  // };

  return (
    <div className="offer-step">
      <div className="offer-step__header">
        <div className="offer-step__title-wrapper">
          <h4 className="offer-step__title">{pageTitle}</h4>
          {showTabs && (
            <Segmented
              value={activeTab}
              onChange={(value) => setActiveTab(value as 'banks' | 'leasing')}
              options={[
                {
                  label: (
                    <div className="offer-step__tab-label-wrapper">
                      <span>Банки</span>
                      {banksPriorityStatus.count > 0 && (
                        <div
                          style={{
                            background: statusColors[banksPriorityStatus.type]
                          }}
                          className="offer-step__tab-label-status"
                        >
                          <span
                            className={`offer-step__label-count ${
                              banksPriorityStatus.type === 'waiting'
                                ? 'offer-step__label-count--dark'
                                : ''
                            }`}
                          >
                            {banksPriorityStatus.count}
                          </span>
                        </div>
                      )}
                    </div>
                  ),
                  value: 'banks'
                },
                {
                  label: (
                    <div className="offer-step__tab-label-wrapper">
                      <span>Лизинг</span>
                      {leasingPriorityStatus.count > 0 && (
                        <div
                          style={{
                            background: statusColors[leasingPriorityStatus.type]
                          }}
                          className="offer-step__tab-label-status"
                        >
                          <span
                            className={`offer-step__label-count ${
                              leasingPriorityStatus.type === 'waiting'
                                ? 'offer-step__label-count--dark'
                                : ''
                            }`}
                          >
                            {leasingPriorityStatus.count}
                          </span>
                        </div>
                      )}
                    </div>
                  ),
                  value: 'leasing'
                }
              ]}
            />
          )}
        </div>
        <div className="offer-step__actions">
          {!isHasFinancedOffer && (
            <>
              {isAdmin && (showTabs ? activeTab : sentTo) === 'banks' && (
                <Button
                  className="offer-step__action offer-step__update-button"
                  size="small"
                  onClick={() => setReCreateModal(true)}
                  disabled={!canResendRequest}
                >
                  <ReloadOutlinedIcon />
                  <p className="offer-step__button-text">
                    Переотправить заявку
                  </p>
                </Button>
              )}

              {/* TODO Ожидается шаблон по отображению списка предложений в PDF */}
              {/* <Button
                className="offer-step__action"
                size="small"
                onClick={handleDownloadPDF}
                isLoading={isDownloading}
                isReversed
              >
                <p className="offer-step__button-text">Скачать PDF</p>
                {!isDownloading && <DownloadIcon />}
              </Button> */}

              <Button
                className="offer-step__action"
                size="small"
                onClick={() => updateResponses()}
                isLoading={responsesUpdating}
                isReversed
              >
                <p className="offer-step__button-text">Обновить</p>
                {!responsesUpdating && <ReloadOutlined />}
              </Button>
            </>
          )}
        </div>
      </div>

      <OfferStepContext.Provider
        value={{
          handleChangeEuBankStatus,
          onAcceptOffer,
          onAcceptLeasingOffer,
          handleCancelResponse,
          handleChangeForteStatus,
          updateResponses,
          onNewAcceptModal: (data) => {
            setNewFocusedBankOffer(data);
            setShowAcceptModal(true);
          }
        }}
      >
        {(showTabs ? activeTab : sentTo) === 'banks' ? (
          <BankOffers
            approvedOffers={banksApprovedOffers}
            waitingOffers={banksWaitingOffers}
            declinedOffers={banksDeclinedOffers}
          />
        ) : (
          <LeasingOffers
            approvedOffers={leasingOffersArrays.approvedOffers}
            waitingOffers={leasingOffersArrays.waitingOffers}
            declinedOffers={leasingOffersArrays.declinedOffers}
          />
        )}
      </OfferStepContext.Provider>

      <CustomModal
        editClass="forte"
        handleCancel={() => setShowInfoForForte(false)}
        handleOk={() => setShowInfoForForte(false)}
        isModalOpen={showInfoForForte}
        title="FORTE"
        footer={[]}
      >
        <p>Отказ со стороны Forte банка</p>
      </CustomModal>
      <AcceptModal
        selectedOffer={selectedOfferModal}
        showAcceptModal={showAcceptModal}
        onSelectBankOffer={
          newFocusedBankOffer ? handleNewSelectOffer : onSelectOffer
        }
        onCancel={() => {
          setShowAcceptModal(false);
          setNewFocusedBankOffer(null);
          setFocusedBankOffer(null);
        }}
        isAcceptLoading={isAcceptLoading}
      />
      <LeasingAcceptModal
        selectedOffer={selectedLeasingOfferModal}
        showAcceptModal={showLeasingAcceptModal}
        onSelectLeasingOffer={onSelectLeasingOffer}
        onCancel={() => {
          setShowLeasingAcceptModal(false);
          setFocusedLeasingOffer(null);
        }}
        isAcceptLoading={isLeasingAcceptLoading}
      />
      <RecreateRequest isOpened={reCreateModal} onClose={setReCreateModal} />
    </div>
  );
};
